import { FIELD_TYPES, SORT_DIRECTIONS } from "../constants";

/**
 * Creates a comparator function for sorting based on field type and direction
 * @param {string} field - The field name to sort by
 * @param {string} direction - Sort direction ('asc' or 'desc')
 * @param {Object} fieldTypes - Map of field names to their types (FIELD_TYPES)
 * @returns {Function} Comparator function for Array.sort()
 */
export const createSortComparator = (field, direction, fieldTypes = {}) => {
  const fieldType = fieldTypes[field] || FIELD_TYPES.STRING;
  const isAscending = direction === SORT_DIRECTIONS.ASC;

  return (a, b) => {
    const aVal = a[field];
    const bVal = b[field];

    // Handle date fields
    if (fieldType === FIELD_TYPES.DATE) {
      const dateA = aVal ? new Date(aVal) : new Date(0);
      const dateB = bVal ? new Date(bVal) : new Date(0);
      return isAscending ? dateA - dateB : dateB - dateA;
    }

    // Handle numeric fields
    if (fieldType === FIELD_TYPES.NUMBER) {
      const numA = aVal != null ? Number(aVal) : 0;
      const numB = bVal != null ? Number(bVal) : 0;
      return isAscending ? numA - numB : numB - numA;
    }

    // Handle array length fields (e.g., analyst field)
    if (fieldType === FIELD_TYPES.ARRAY_LENGTH) {
      const getLength = (val) => {
        if (!val) return 0; // null/undefined
        if (Array.isArray(val)) return val.length;
        return 1; // single object
      };

      const aLength = getLength(aVal);
      const bLength = getLength(bVal);

      // Compare lengths: 0 -> 1 -> 2 -> 3 -> ...
      return isAscending ? aLength - bLength : bLength - aLength;
    }

    // Handle string fields (default)
    // Handle null/undefined values
    if (!aVal && !bVal) return 0;
    if (!aVal) return isAscending ? 1 : -1;
    if (!bVal) return isAscending ? -1 : 1;

    // Compare string values
    return isAscending
      ? aVal.toString().localeCompare(bVal.toString())
      : bVal.toString().localeCompare(aVal.toString());
  };
};

/**
 * Sorts an array of objects based on sort configuration
 * @param {Array} items - Array of items to sort
 * @param {Object} sortConfig - Sort configuration object
 * @param {string} sortConfig.field - Field name to sort by
 * @param {string} sortConfig.direction - Sort direction ('asc' or 'desc')
 * @param {Object} fieldTypes - Map of field names to their types (FIELD_TYPES)
 * @returns {Array} Sorted array (new array, original is not modified)
 */
export const sortByConfig = (items, sortConfig, fieldTypes = {}) => {
  if (!items || items.length === 0) return items;
  if (!sortConfig?.field) return items;

  const comparator = createSortComparator(sortConfig.field, sortConfig.direction, fieldTypes);
  return [...items].sort(comparator);
};

/**
 * Sort visit details by order_id.
 * Details with order_id 999 (not in template) are sorted alphabetically by category name.
 * Details with order_id 999 appear after all details with valid order_ids.
 *
 * @param {Array} details - Array of visit detail objects with order_id and visit_detail_category
 * @returns {Array} - Sorted array of visit details
 */
export const sortVisitDetails = (details) => {
  return [...details].sort((a, b) => {
    const orderIdA = a.order_id ?? 999;
    const orderIdB = b.order_id ?? 999;

    // If both have order_id 999, sort alphabetically by category name
    if (orderIdA === 999 && orderIdB === 999) {
      const categoryA = a.visit_detail_category || "";
      const categoryB = b.visit_detail_category || "";
      return categoryA.localeCompare(categoryB);
    }

    // If one has 999 and the other doesn't, 999 comes after
    if (orderIdA === 999) return 1;
    if (orderIdB === 999) return -1;

    // Otherwise, sort by order_id
    return orderIdA - orderIdB;
  });
};
